import ContactTopic from './ContactTopic';
import { isNumber, sanitizePhone, zeroFill } from 'lib/util/helpers';
import { IOpenHoursConfig } from './models';
import doT from 'bootstrap/load-dot';

export function trackSelectionChange(topic : ContactTopic) {
	if (!topic || !(SCS && SCS.portal && SCS.portal.datalayer)) {
		return;
	}
	const label = topic.topicTrackingLabel || topic.topicName || '';
	const topicParent = topic.parent as ContactTopic;
	let topicParentTrackingName = '';
	if (topicParent) {
		topicParentTrackingName = topicParent.topicTrackingCategory ?
			topicParent.topicTrackingCategory : topicParent.topicTrackingLabel;
	}
	const category = topic.topicTrackingCategory ||
		topicParentTrackingName ||
		topic.parentTopicName ||
		topic.topicName;

	SCS.portal.datalayer.trackEvent('select', label, 'contactform', category, 'Contact Wizard');
}

export function validatePhoneNumber(phonestr : string) : boolean {
	// Regex used from Java validators
	const testRegexp = /^\+41(?!800|840|842|844|848|860|868|869|878|900|901|906|97|98|99)\d{9}$/;

	return testRegexp.test(phonestr);
}

export function getValidSanitizePhoneNumber(phone : string) : string | undefined {
	const sanitazedNumber = sanitizePhone(phone);
	if(!validatePhoneNumber(sanitazedNumber)) {
		return undefined;
	}

	return sanitazedNumber;
}

export function createOpenHoursConfig(str : string) : IOpenHoursConfig {
	const retobj : IOpenHoursConfig  = {
		exceptionallyClosed: [],
		exceptionallyOpen: [],
		openhours: [],
	};
	str.split(';').forEach(elem => {
		elem = elem.trim();
		if(isNumber(elem.charAt(0))) {
			if(elem.indexOf('closed') >= 0) {
				retobj.exceptionallyClosed.push(elem);
			} else {
				retobj.exceptionallyOpen.push(elem);
			}
		} else {
			retobj.openhours.push(elem);
		}
	});

	return retobj;
}

const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function getOpenWeekdaysList(weekDayRange : string) : number[] {
	const startEndDay = weekDayRange.split('-');
	let startDay = weekDays.indexOf(startEndDay[0]);
	let endDay = weekDays.indexOf(startEndDay[1]);
	const openDays = [];

	let i = startDay;
	while(startDay !== -1 || endDay !== -1) {
		if(i === startDay) { startDay = -1; }
		if(i === endDay) { endDay = -1; }
		openDays.push(i);
		i = (i + 1) % 7;
	}

	return openDays;
}

function isOpenWeekday(weekDay : number, weekDayRange : string) : boolean {
	const openWeekDays = getOpenWeekdaysList(weekDayRange);

	return openWeekDays.includes(weekDay);
}

function isOpenToday(today : Date, openingHours : string[]) : boolean {
	return openingHours.some(openConfig => {
		const weekDayRange = openConfig.split(' ')[0];
		const hoursRange = openConfig.split(' ')[1];
		if(isOpenWeekday(today.getDay(), weekDayRange)) {
			return isInsideOpeningHours(today, hoursRange);
		}

		return false;
	});
}
const hourRangePattern = /^(\d{2})\:(\d{2})\-(\d{2})\:(\d{2})$/;

function isInsideOpeningHours(today : Date, hoursRange : string) : boolean {
	const hours = hourRangePattern.exec(hoursRange)!.map(e => parseInt(e, 10));
	const open = new Date(today);
	const close = new Date(today);

	open.setHours(hours[1]);
	open.setMinutes(hours[2]);

	close.setHours(hours[3]);
	close.setMinutes(hours[4]);

	return (today.getTime() >= open.getTime() && today.getTime() < close.getTime());
}

function getFormatedDate(today : Date) : string {
	return `${zeroFill(today.getDate())}.${zeroFill(today.getMonth() + 1)}.${today.getFullYear()}`;
}

function isExceptionallyClosedToday(today : Date, exceptions : string[]) : boolean {
	// Exception format: 21.05.2018 closed
	const todayDate : string = getFormatedDate(today);

	return exceptions.some(exception => exception.split(' ')[0] === todayDate);
}

interface IExceptionallyConfigured {
	open : boolean;
	hasException : boolean;
}

function isTodayExceptionallyConfigured(today : Date, exceptions : string[]) : IExceptionallyConfigured {
	// Exception format: 02.01.2019 07:00-19:59
	const todayDate : string = getFormatedDate(today);

	let exceptionHourRange = '';
	let open = false;
	let hasException = false;

	exceptions.some(exception => {
		const exceptionArr = exception.split(' ');
		if (exceptionArr[0] === todayDate) {
			exceptionHourRange = exceptionArr[1];

			return true;
		}

		return false;
	});

	if (exceptionHourRange) {
		open = isInsideOpeningHours(today, exceptionHourRange);
		hasException = true;
	}

	return { open, hasException };
}

// Parameter today is used for testing in every other case it should be ommitted
export function isOpen(config : IOpenHoursConfig, today = new Date()) : boolean {
	if(isExceptionallyClosedToday(today, config.exceptionallyClosed)) {
		return false;
	}
	const exceptionallyConfigured = isTodayExceptionallyConfigured(today, config.exceptionallyOpen);
	if(exceptionallyConfigured.hasException) {
		return exceptionallyConfigured.open;
	}

	return isOpenToday(today, config.openhours);
}

interface IWeekDaysTranslations {
	[propName : string] : string[];
}

const weekDaysTranslations : IWeekDaysTranslations = {
	Fr : ['Fr', 'Ve', 'Ve', 'Fr'],
	Mo: ['Mo', 'Lu', 'Lu', 'Mo'],
	Sa: ['Sa', 'Sa', 'Sa', 'Sa'],
	Su: ['So', 'Di', 'Do', 'Su'],
	Th: ['Do', 'Je', 'Gi', 'Th'],
	Tu: ['Di', 'Ma', 'Ma', 'Tu' ],
	We: ['Mi', 'Me', 'Me', 'We'],
};

const langs = ['de', 'fr', 'it', 'en'];

export function formatCloseTime(openingHours : string, closeLabel : string, lang : string) : JQuery {
	const config = createOpenHoursConfig(openingHours);
	const template = '<p>{{~it.openhours :value}}{{! value}}<br/>{{~}}{{~it.exceptions :value}}{{! value}}<br/>{{~}}</p>';
	const langPos = langs.indexOf(lang);
	config.openhours = config.openhours.map(e =>
		e.replace(/(Mo|Tu|We|Th|Fr|Sa|Su)/g, match => weekDaysTranslations[match][langPos]));
	config.exceptionallyClosed.map(e => e.replace('closed', closeLabel));

	const today = new Date();
	const endDay = new Date();
	endDay.setDate(today.getDate() + 10);

	const exceptions = config.exceptionallyClosed.concat(config.exceptionallyOpen)
	.filter(el => {
		const excDate = new Date(el.split(' ')[0]);

		return excDate.getTime() >= today.getTime() && excDate.getTime() <= endDay.getTime();
	})
	.sort((a : string, b : string) => new Date(a.split(' ')[0]).getTime() - new Date(b.split(' ')[0]).getTime());

	const tmpl = doT.compile(template);

	return $(tmpl({
		exception : exceptions,
		openhours : config.openhours,
	}));
}

export function isABCSupported() : boolean {
	const macOSVer = [10, 13, 4];
	const iOSVer = [11, 3];

	// Regex used from apple business chat: https://static.cdn-apple.com/businesschat/start-chat-button/1/index.js
	const regex = /\((Macintosh|iPhone|iPad|(?:iPod(?:\x20touch)?));.*\x20((?:\d[_.]{0,1})+)[^)]*\)\x20/i;
	const testStr = window.navigator && window.navigator.userAgent || '';

	const regexResult =  regex.exec(testStr);
	if(!regexResult) { return false; }

	const supportedVer = regexResult[1].toUpperCase() === 'MACINTOSH' ? macOSVer : iOSVer;
	const currentVer = regexResult[2].split('_').map(elem => parseInt(elem, 10));

	for(let i = 0; i < supportedVer.length; i += 1) {
		const version = currentVer[i] || 0;

		if(version === supportedVer[i]) {
			// Check next minor versions
			continue;
		}

		return version > supportedVer[i];
	}

	// If the versions are identical
	return true;
}

export function trackCtx(evt : JQuery.ClickEvent) {
	const $ctx = $(evt.currentTarget).closest('.tracking-info-holder');
	if(SCS && SCS.portal && SCS.portal.datalayer) {
		new SCS.portal.datalayer.Interaction()
			.withEvent(
				$ctx.data('track-action'),
				$ctx.data('track-label'),
				$ctx.data('track-location'),
				$ctx.data('track-category'),
				$ctx.data('track-linkname'),
			)
			.withConversion(
				$ctx.data('conversion-category'),
				$ctx.data('conversion-detail'),
				$ctx.data('conversion-class'),
			)
			.track();
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/contact/helpers.ts